import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { baseURL, tenantId } from "../config/api";
import { saveAs } from "file-saver";
import useWebData from "../hooks/useWebData";

type ImagePopupProps = {
    handlePopup: () => void;
    showPopup: boolean;
    setPopup: (value: boolean) => void;
    fileName: FileList | null;
    setFile: (value: FileList | null) => void;
    fileSize: number;
    setFileSize: (value: number) => void;
    setFileName: (name: string) => void;
    filePath: string;
    saved: boolean;
    item: any;
}

export const ImagePopup = ({
    showPopup,
    setPopup,
    handlePopup,
    fileName,
    setFile,
    fileSize,
    setFileSize,
    setFileName,
    filePath,
    saved,
    item
}: ImagePopupProps) => {  
    const { webData }: any = useWebData();
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const [isSaved, setSaved] = useState<boolean>(saved);
    const [newFileName, setNewFileName] = useState<string>("");
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const inputFileRef = useRef<HTMLInputElement>(null);


    useEffect(() => {
        if (showPopup) {
            document.body.classList.add("overflow-hidden");
        } else {
            document.body.classList.remove("overflow-hidden");
        }
        return () => document.body.classList.remove("overflow-hidden");

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPopup]);

    if (!showPopup) return null;

    const saveTheFile = () => {
        if (fileName) {
            if (!isProcessing) {
                setIsProcessing(true);
                const formData = new FormData();
                formData.append("ImageFile", fileName[0]);

                axios
                    .post(`${baseURL}customerFile/upload`, formData, {
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "multipart/form-data"
                        },
                        withCredentials: true,
                    })
                    .then((response) => {
                        setSaved(true);
                        setFileName(response.data);
                        setNewFileName(response.data);
                        if (item.type.split(" ")[0] === "PUT") {
                            axios
                                .put(
                                    `${baseURL}customerExtraInformation?userId=${0}`,
                                    JSON.stringify({
                                        id: item.type.split(" ")[1],
                                        idNumber: item.idNumber,
                                        issueDate: new Date(item.issueDate),
                                        exprationDate: new Date(item.exprationDate),
                                        filePath: response.data,
                                        idTypeId: item.idTypeId,
                                        customerId: item.customerId,
                                        imageSize: fileSize,

                                    }),
                                    {
                                        headers: {
                                            'X-Tenant-ID': tenantId,
                                            "Content-Type": "application/json"
                                        },
                                        withCredentials: true,
                                    }
                                )
                                .then(() => {
                                    handlePopup();
                                    setIsProcessing(false);
                                })
                                .catch((error) => console.error("Error:", error));
                        }

                    })
                    .catch((error) => {
                        console.error("Error:", error);
                    });
            }
        }
    };

    const onFileChangeCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
        /*Selected files data can be collected here.*/
        const selectedFiles = e.target.files as FileList;
        if (selectedFiles.length !== 0) {
            setFile(selectedFiles);
            setFileSize(selectedFiles[0].size);
            setSaved(false);

        }
    };

    const downloadTheImage = () => {
        saveAs(
            `${baseURL}customerFile/download?name=${!saved ? newFileName : filePath}`,
            `${!saved ? newFileName : filePath}.png`
        );
    };

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div
                    className={`w-full ${isFullScreen ? "h-full" : "max-w-3xl"} bg-white rounded-lg shadow-xl overflow-auto`}
                >
                    <div className="p-4">
                        {/* Header */}
                        <div className="flex justify-between items-center border-b pb-2">
                            <h2 className="text-lg font-semibold">Preview</h2>
                            <button
                                className="text-gray-600 hover:text-gray-800 text-xl"
                                onClick={() => {
                                    setIsFullScreen(false);
                                    handlePopup();
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        {/* Content */}
                        <div className="mt-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
                                <div className="space-y-4">
                                    <div>
                                        <span className="text-xs font-medium text-gray-700">Filename</span>
                                        <br />
                                        <span className="text-xs font-medium text-gray-500">
                                            {!isSaved ? fileName?.[0]?.name : filePath}
                                        </span>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <span className="text-xs font-medium text-gray-700">Size</span>
                                        <br />
                                        <span className="text-xs font-medium text-gray-500">
                                            {fileSize === 0 ? 0 : (fileSize / 1024).toFixed(2)} KB
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Image Preview */}
                            <div className="w-full mt-6">
                                <img
                                    src={
                                        !isSaved
                                            ? fileName?.[0] && URL.createObjectURL(fileName[0])
                                            : `${baseURL}/customerFile/Get?name=${filePath}`
                                    }
                                    alt="info"
                                    className={`w-full object-contain ${isFullScreen ? "h-[calc(100vh-200px)]" : "h-[300px]"}`}
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row mt-6 gap-3">
                                <button
                                    type="button"
                                    className="text-white px-4 py-2 rounded"
                                    style={{
                                        backgroundColor: webData?.websiteHeaderColor
                                    }}
                                    onClick={() => setIsFullScreen(!isFullScreen)}
                                >
                                    {isFullScreen && "Exit "}Full Screen
                                </button>

                                <button
                                    type="button"
                                    className="text-white px-4 py-2 rounded"
                                    style={{
                                        backgroundColor: webData?.websiteHeaderColor
                                    }}
                                    onClick={() => {
                                        if (inputFileRef.current) {
                                            inputFileRef.current.value = ""
                                        }
                                        inputFileRef.current?.click()
                                    }}
                                >
                                    Upload New File
                                </button>

                                {isSaved ? (
                                    <>
                                        <button
                                            type="button"
                                            className="text-white px-4 py-2 rounded"
                                            style={{
                                                backgroundColor: webData?.websiteHeaderColor
                                            }}
                                            onClick={downloadTheImage}
                                        >
                                            Download
                                        </button>


                                    </>
                                ) : (
                                    <button
                                        type="button"
                                        className="text-white px-4 py-2 rounded"
                                        style={{
                                            backgroundColor: webData?.websiteHeaderColor
                                        }}
                                        onClick={saveTheFile}
                                        disabled={isProcessing}
                                    >
                                        {isProcessing ? "Processing..." : "Save"}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <input
                type="file"
                accept="image/*"
                ref={inputFileRef}
                className="hidden"
                onChange={onFileChangeCapture}
            />
        </>

    )
}